import React, { FC } from 'react';
import { Text, Box, AvatarGroup, AvatarSize, AvatarGroupSpacing, Icon, IconSize } from '@bechtel/gds';
import { useName, RestrictTo, useDate } from '@bechtel/client-common';
import { map } from 'lodash';
import { RepeatButtonStyled, CloneContainerStyled } from './styled';
import { Trans, t } from '@lingui/macro';
import { parseISO } from 'date-fns';
import { CrewMember, RodRequest, RodRequestStatusCode } from '@bechtel/types';

interface CloneRodRequestProps {
  cloneRodRequest: (rodRequest: RodRequest) => void;
  rodRequest: RodRequest;
}

export const CloneRodRequest: FC<CloneRodRequestProps> = ({ rodRequest, cloneRodRequest }) => {
  const { getFullName } = useName();
  const { formatRelativeInWords } = useDate();
  const isActiveRequest = !(
    rodRequest?.currentStatusI18n === RodRequestStatusCode.CLOSED ||
    rodRequest?.currentStatusI18n === RodRequestStatusCode.EXPIRED
  );
  const getStencils = (stencils: CrewMember[]) => {
    return map(stencils, (stencil) => {
      return {
        src: stencil?.imageUrl ? stencil?.imageUrl : '',
        alt: getFullName(stencil?.firstName ? stencil.firstName : ' ', stencil?.lastName ? stencil.lastName : ' '),
      };
    });
  };

  return (
    <CloneContainerStyled data-testid="clone-container">
      <Box flexDirection={{ sm: 'column' }} ml={{ sm: '500' }} data-testid="request-date">
        <Text size="300" color="bechtel.blue.800" family="graphik" weight="600">
          {formatRelativeInWords(parseISO(rodRequest?.createdAt), new Date(), RestrictTo.Day, 'dd MMM yyyy', ``, false)}
        </Text>

        <Box mt={{ sm: '300' }}>
          <Text size="200" color="bechtel.blue.800" family="graphik" style={{ opacity: 0.6 }}>
            {formatRelativeInWords(parseISO(rodRequest?.createdAt), new Date(), RestrictTo.Day, 'HH:mm aa', ``, false)}
          </Text>
        </Box>
      </Box>

      <Box flexDirection={{ sm: 'column' }} data-testid="weld-procedure">
        <Text size="400" color="grey.700" weight="600">
          {rodRequest?.weldProcedure.procedure}
        </Text>

        <Box alignItems={{ sm: 'center' }} mt={{ sm: '300' }}>
          <Box mr={{ sm: '550' }}>
            <AvatarGroup
              data-testid="stencils-avatar-group"
              maxCount={2}
              data={getStencils(map(rodRequest.stencils, (item) => item.stencil))}
              size={AvatarSize.MEDIUM}
              spacing={AvatarGroupSpacing.MEDIUM}
            />
          </Box>
          <Box flexDirection={{ sm: 'column' }}>
            <Text size="300" color="bechtel.blue.700" weight="600">
              {isActiveRequest ? t`Active` : t`Closed`}
            </Text>
            <Box mt={{ sm: '100' }}>
              {rodRequest?.createdAt && (
                <Text size="200" color="bechtel.blue.700">
                  {formatRelativeInWords(
                    parseISO(rodRequest?.createdAt),
                    new Date(),
                    RestrictTo.Day,
                    'HH:mm aa',
                    ``,
                    true
                  )}
                </Text>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
      <Box mr={{ sm: '500' }} data-testid="repeat-button" alignItems={{ sm: 'center' }}>
        <RepeatButtonStyled
          onClick={(e) => {
            e.preventDefault();
            cloneRodRequest(rodRequest);
          }}
        >
          <Icon icon="redo" color="bechtel.blue.800" size={IconSize.MEDIUM} />
          <Box ml={{ sm: '300' }}>
            <Text size="400" color="bechtel.blue.800" weight="600">
              <Trans>Repeat</Trans>
            </Text>
          </Box>
        </RepeatButtonStyled>
      </Box>
    </CloneContainerStyled>
  );
};
